import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import {
  HeadingsRow,
  PageHeading,
  SrOnly,
  TeamHeading,
} from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import { MinimalChannelResource } from '@/types/resources';
import { api } from '@/utils/api';

import SettingsNavigation from '../components/SettingsNavigation';
import WatermarkForm from './WatermarkForm';
import WatermarkRow from './WatermarkRow';

const StyledTable = styled(Table)`
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  th,
  td {
    &:first-child {
      vertical-align: top;
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 300px;
    }
  }
  td {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`;

const Watermarks = () => {
  const { teamId } = useParams();

  const [showCreationForm, setShowCreationForm] = useState(false);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 400);

  const {
    data: channels,
    isLoading,
    isError,
  } = useQuery<MinimalChannelResource[]>(
    [
      'watermarks',
      {
        teamId,
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    () =>
      api.get<never, MinimalChannelResource[]>(`/v1/channels/watermarked`, {
        params: {
          'filter[team_id]': teamId,
          ...(!!debouncedSearch && {
            'filter[search]': debouncedSearch,
          }),
        },
      }),
    {
      staleTime: 1000 * 120,
    },
  );

  return (
    <>
      <HeadingsRow>
        <div>
          <TeamHeading />
          <PageHeading>Settings</PageHeading>
        </div>
        <Button onClick={() => setShowCreationForm(true)}>
          Create Watermark
        </Button>
      </HeadingsRow>
      <SettingsNavigation />
      <AnimatePresence>
        {showCreationForm && (
          <WatermarkForm onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by channel name"
      />
      <StyledTable $fixed>
        <thead>
          <tr>
            <Th>Channel name</Th>
            <Th>Watermark</Th>
            <Th $actionsCol>
              <SrOnly>Actions</SrOnly>
            </Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            colSpan={3}
            isLoading={isLoading}
            hasData={!!channels?.length}
            isError={isError}
          >
            {channels?.map((channel) => (
              <WatermarkRow key={channel.id} channel={channel} />
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
    </>
  );
};

export default Watermarks;
